import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Form, Spinner, Button } from 'react-bootstrap';

const MonthSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [otherInvoices, setOtherInvoices] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthsData, setMonthsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [hideInactive, setHideInactive] = useState(false);

  const months = [
    { value: '01', label: t('january') },
    { value: '02', label: t('february') },
    { value: '03', label: t('march') },
    { value: '04', label: t('april') },
    { value: '05', label: t('may') },
    { value: '06', label: t('june') },
    { value: '07', label: t('july') },
    { value: '08', label: t('august') },
    { value: '09', label: t('september') },
    { value: '10', label: t('october') },
    { value: '11', label: t('november') },
    { value: '12', label: t('december') }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Завантаження даних тижнів з сервера
  const fetchMonthsData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get('/api/monthly-reports/months', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMonthsData(response.data.data);
    } catch (err) {
      console.error('Помилка завантаження даних тижнів:', err);
    }
  };

  // Отримання тижнів для обраного місяця та року
  const getWeeksForSelectedMonth = () => {
    if (!monthsData || !selectedMonth || !selectedYear) {
      return [];
    }
    
    const yearData = monthsData[selectedYear.toString()];
    if (!yearData) {
      return [];
    }
    
    return yearData[selectedMonth] || [];
  };

  // Завантаження статистики для всіх користувачів
  const fetchStats = async () => {
    if (!selectedMonth || !selectedYear) {
      return;
    }

    setLoading(true);
    try {
      const weeks = getWeeksForSelectedMonth();
      console.log('🔍 MonthSummary - selectedMonth:', selectedMonth);
      console.log('🔍 MonthSummary - selectedYear:', selectedYear);
      console.log('🔍 MonthSummary - monthsData:', monthsData);
      console.log('🔍 MonthSummary - weeks for selected month:', weeks);
      
      const token = localStorage.getItem("token");
      const response = await axios.get('/api/month-summary', {
        params: {
          month: selectedMonth,
          year: selectedYear,
          weeks: weeks.length ? weeks.join(',') : ''
        },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      console.log('📊 MonthSummary - отримані дані:', response.data);
      const data = response.data;
      if (Array.isArray(data)) {
        setStats(data);
        setOtherInvoices([]);
      } else {
        setStats(data.drivers || []);
        setOtherInvoices(data.otherInvoices || []);
      }
    } catch (err) {
      console.error('Помилка завантаження статистики:', err);
      setStats([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthsData();
    
    // Завантажуємо збережені значення місяця та року з localStorage
    const savedMonth = localStorage.getItem('selectedMonth');
    const savedYear = localStorage.getItem('selectedYear');
    
    if (savedMonth) {
      setSelectedMonth(savedMonth);
    }
    if (savedYear) {
      setSelectedYear(parseInt(savedYear));
    }
  }, []);

  useEffect(() => {
    if (selectedMonth && selectedYear) {
      fetchStats();
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    const savedHideInactive = localStorage.getItem('hideInactiveMonth');
    if (savedHideInactive !== null) {
      setHideInactive(savedHideInactive === 'true');
    }
  }, []);

  // Зберігаємо вибрані значення в localStorage при зміні
  useEffect(() => {
    if (selectedMonth) {
      localStorage.setItem('selectedMonth', selectedMonth);
    }
  }, [selectedMonth]);

  useEffect(() => {
    if (selectedYear) {
      localStorage.setItem('selectedYear', selectedYear.toString());
    }
  }, [selectedYear]);

  const sortData = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Порівняння масивів номерів рахунків (по 2 на водія): спочатку 1-й номер, потім 2-й (числова порівняння)
  const compareInvoiceNumbers = (a, b) => {
    const toNum = (v) => (v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : null);
    const numsA = (a.invoices && a.invoices.length)
      ? a.invoices.map((inv) => toNum(inv.invoice_number))
      : [];
    const numsB = (b.invoices && b.invoices.length)
      ? b.invoices.map((inv) => toNum(inv.invoice_number))
      : [];
    const emptyLast = sortConfig.direction === 'ascending';
    if (numsA.length === 0 && numsB.length === 0) return 0;
    if (numsA.length === 0) return emptyLast ? 1 : -1;
    if (numsB.length === 0) return emptyLast ? -1 : 1;
    const len = Math.max(numsA.length, numsB.length);
    for (let i = 0; i < len; i++) {
      const va = numsA[i] ?? null;
      const vb = numsB[i] ?? null;
      const cmp = (va === null ? -1 : va) - (vb === null ? -1 : vb);
      if (cmp !== 0) return sortConfig.direction === 'ascending' ? (cmp < 0 ? -1 : 1) : (cmp < 0 ? 1 : -1);
    }
    return 0;
  };

  const sortedStats = React.useMemo(() => {
    let sortableStats = [...stats];
    if (sortConfig.key !== null) {
      sortableStats.sort((a, b) => {
        if (sortConfig.key === 'invoiceNumbers') {
          return compareInvoiceNumbers(a, b);
        }
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableStats;
  }, [stats, sortConfig]);

  const getSortEmoji = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? ' 🔼' : ' 🔽';
    }
    return '';
  };

  const handleAddInvoice = (userId) => {
    navigate(`/admin/monthly-reports?userId=${userId}&month=${selectedMonth}&year=${selectedYear}`);
  };

  const handleViewInvoices = (userId) => {
    navigate(`/admin/generate-invoice?driverId=${userId}&month=${selectedMonth}&year=${selectedYear}`);
  };

  const handleCreateInvoiceFromScratch = () => {
    navigate(`/admin/invoice-constructor?month=${selectedMonth}&year=${selectedYear}`);
  };

  const handleEditOtherInvoice = (invoiceId) => {
    navigate(`/admin/invoice-constructor?invoiceId=${invoiceId}`);
  };

  return (
    <div className="container">
      <h2>📊 {t("monthlyReports")} - Зведена таблиця</h2>

      <div className="row mb-3">
        <div className="col-md-4">
          <label className="form-label fw-bold">{t("selectMonth")}</label>
          <Form.Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="">{t("selectMonth")}</option>
            {months.map(month => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold">{t("year")}</label>
          <Form.Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {years.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold">&nbsp;</label>
          <button
            className="btn btn-primary w-100"
            onClick={fetchStats}
            disabled={!selectedMonth || !selectedYear || loading}
          >
            {loading ? t("loading") : t("loaddata")}
          </button>
        </div>
      </div>

      {/* Тижні для обраного місяця */}
      {selectedMonth && selectedYear && getWeeksForSelectedMonth().length > 0 && (
        <div className="alert alert-info">
          <strong>Тижні місяця:</strong> {getWeeksForSelectedMonth().join(', ')}
        </div>
      )}
      {selectedMonth && selectedYear && monthsData && getWeeksForSelectedMonth().length === 0 && (
        <div className="alert alert-warning">
          Для цього місяця не налаштовано тижні в системі — суми з рапортів будуть 0; у таблиці показано всіх водіїв і всі рахунки за обраний місяць.
        </div>
      )}

      <div
        className="mt-3 border border-primary rounded"
        style={{
          backgroundColor: "#f8f9fa",
          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div className="d-flex align-items-center px-3 py-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="hideInactiveCheckbox"
            checked={hideInactive}
            style={{
              transform: "scale(1.5)",
              cursor: "pointer",
              marginRight: "15px"
            }}
            onChange={() => {
              setHideInactive(prev => {
                const newValue = !prev;
                localStorage.setItem('hideInactiveMonth', newValue);
                return newValue;
              });
            }}
          />
          <label
            className="form-check-label fw-bold m-0"
            htmlFor="hideInactiveCheckbox"
            style={{ fontSize: "1.1rem" }}
          >
            🚫 Сховати неактивних (без рапортів і без рахунків за місяць)
          </label>
        </div>
      </div>

      {loading && (
        <div className="text-center my-4">
          <Spinner animation="border" />
          <p className="mt-2">{t("loading")}...</p>
        </div>
      )}

      {sortedStats.length > 0 && (
        <table className="table table-bordered mt-4">
          <thead className="table-white text-center sticky-top bg-dark" style={{ zIndex: 10 }}>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => sortData('firstName')}>
                {t("name")}{getSortEmoji('firstName')}
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => sortData('lastName')}>
                {t("surname")}{getSortEmoji('lastName')}
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => sortData('totalAccountant')}>
                Accountant{getSortEmoji('totalAccountant')}
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => sortData('totalTaxesZUS')}>
                Taxes ZUS{getSortEmoji('totalTaxesZUS')}
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => sortData('savedInvoicesCount')}>
                Рахунки{getSortEmoji('savedInvoicesCount')}
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => sortData('invoiceNumbers')}>
                Номери рахунків{getSortEmoji('invoiceNumbers')}
              </th>
            </tr>
          </thead>

          <tbody>
            {sortedStats
              .filter(user => {
                if (!hideInactive) return true;
                return user.weeksCount > 0 || (user.savedInvoicesCount || 0) > 0;
              })
              .map(user => (
                <tr
                  key={user.id}
                  className={
                    user.weeksCount > 0
                      ? "table-success"
                      : (user.savedInvoicesCount || 0) > 0
                        ? "table-warning"
                        : "table-danger"
                  }
                >
                  <td>{user.firstName}</td>
                  <td>{user.lastName}</td>
                  <td className="text-end">{(user.totalAccountant || 0).toFixed(2)} zł</td>
                  <td className="text-end">{(user.totalTaxesZUS || 0).toFixed(2)} zł</td>
                  <td className="text-center">
                    {user.savedInvoicesCount === 0 ? (
                      <span 
                        onClick={() => handleAddInvoice(user.id)}
                        style={{ 
                          fontSize: '1.5em', 
                          cursor: 'pointer',
                          color: '#28a745',
                          fontWeight: 'bold'
                        }}
                        title="Додати рахунок"
                      >
                        ➕
                      </span>
                    ) : (
                      <span 
                        onClick={() => handleViewInvoices(user.id)}
                        style={{ 
                          cursor: 'pointer',
                          display: 'inline-block'
                        }}
                        title="Переглянути рахунки"
                      >
                        {Array.from({ length: user.savedInvoicesCount }, (_, index) => (
                          <span key={index} style={{ fontSize: '1.2em', marginRight: '2px' }}>✅</span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td>
                    {user.invoices && user.invoices.length > 0 ? (
                      <div style={{ fontSize: '0.9em' }}>
                        {user.invoices.map((invoice, idx) => (
                          <span key={idx}>
                            <span className="badge bg-secondary me-1">
                              #{invoice.invoice_number}
                            </span>
                            <small className="text-muted">
                              ({invoice.payment_method === 'transfer' ? 'Переказ' : 'Готівка'})
                            </small>
                            {idx < user.invoices.length - 1 && <br />}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}

      {sortedStats.length === 0 && selectedMonth && selectedYear && !loading && (
        <p className="mt-4">{t("noRecords")}</p>
      )}

      {selectedMonth && selectedYear && (
        <div className="mt-4 p-3 border rounded bg-light">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">📄 Рахунки без прив&#39;язки до водія</h5>
            <Button variant="primary" onClick={handleCreateInvoiceFromScratch}>
              ➕ Створити рахунок (без рапортів)
            </Button>
          </div>
          {otherInvoices.length > 0 ? (
            <table className="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th>Номер</th>
                  <th>Спосіб оплати</th>
                  <th>Статус</th>
                  <th>Дії</th>
                </tr>
              </thead>
              <tbody>
                {otherInvoices.map((inv) => (
                  <tr key={inv.id}>
                    <td>#{inv.invoice_number}</td>
                    <td>{inv.payment_method === 'transfer' ? 'Переказ' : 'Готівка'}</td>
                    <td><span className="badge bg-secondary">{inv.status || 'draft'}</span></td>
                    <td>
                      <Button variant="outline-primary" size="sm" onClick={() => handleEditOtherInvoice(inv.id)}>
                        Редагувати
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-muted mb-0">Немає рахунків, створених без рапортів. Натисніть кнопку вище, щоб створити.</p>
          )}
        </div>
      )}

      {!selectedMonth && !selectedYear && (
        <p className="mt-4">Виберіть місяць та рік для перегляду статистики</p>
      )}
    </div>
  );
};

export default MonthSummary;

